import { getOrganizationList } from '@api/organization.js';
import { fetchUserDetails } from '@api/user.js';
import { IS_APP } from '@constants/global.js';
import Routes from '@router/routes.js';
import { resolveRetainedRoute } from '@router/retainedRoutes.js';
import OrganizationState from '@states/Organization.js';
import UserState from '@states/User.js';
import { logout } from '@utils/logoutHelper.js';
import UniversalCookie from 'universal-cookie';

let instance;

const getInstance = () => {
  if (!instance) {
    instance = {
      subscribers: [],
      // other properties and methods
    };
  }
  return instance;
};

const { subscribers } = getInstance();

let currentPage = null;
let previousRoute = null;
const cookies = new UniversalCookie();
const tokenGetter = () => cookies.get('token');
const setPreviousRoute = (route, search = window.location.search) => {
  if (route.includes('?')) {
    previousRoute = route;
    return;
  }
  const urlParams = new URLSearchParams(search);
  const fullRoute =
    route + (urlParams.toString() ? `?${urlParams.toString()}` : '');
  previousRoute = fullRoute;
};

const isAdmin = () => {
  const user = UserState.user;
  if (!user) return false;
  const selectedOrg = user.selectedOrg;
  const orgData = user.orgData;
  if (!orgData) return false;
  const selectedOrgData = orgData.find(
    org => org.organizationId === selectedOrg,
  );
  return selectedOrgData?.role === 'admin';
};

const subscribe = callback => {
  subscribers.push(callback);
};

const notifySubscribers = (content, title) => {
  subscribers.forEach(subscriber => {
    subscriber(content, title);
  });
};

const isValidJWT = token => {
  const parts = token.split('.');
  if (parts.length !== 3) {
    return false;
  }

  try {
    const header = JSON.parse(atob(parts[0]));
    return header.alg && header.typ === 'JWT';
  } catch (error) {
    return false;
  }
};

const handleURLToken = async () => {
  const urlParams = new URLSearchParams(window.location.search);
  const tokenFromUrl = urlParams.get('token');
  const urlHasToken =
    tokenFromUrl &&
    typeof tokenFromUrl === 'string' &&
    tokenFromUrl.trim() !== '';
  if (urlHasToken && isValidJWT(tokenFromUrl)) {
    cookies.remove('token');
    cookies.set(
      'token',
      tokenFromUrl,
      { expires: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000) }, // 7 days
    );
    urlParams.delete('token');
    const remainingURLParams =
      urlParams.toString().length > 0 ? `?${urlParams.toString()}` : '';
    window.history.replaceState(
      {},
      document.title,
      `${window.location.pathname}${remainingURLParams}`,
    );
  }
};

const parseRouteName = (path, search = window.location.search) => {
  const isLoggedIn = tokenGetter();
  const routeName = resolveRetainedRoute(path, search, Boolean(isLoggedIn));

  const route = Routes[routeName];
  if (!route) return '/404';

  // const hasProjectId = path === '/projects' && urlParams.get('projectId');
  // if (hasProjectId) return '/projects';

  // Handle authentication requirements
  if ((route.needsLogin || window.isLogoutInProgress) && !isLoggedIn) {
    if (!window.isLogoutInProgress) {
      setPreviousRoute(path, search);
      if (window.location.search) {
        window.history.replaceState(
          {},
          document.title,
          window.location.pathname,
        );
      }
    }
    return '/login';
  }

  return routeName;
};

const getUserDetails = async () => {
  try {
    if (!tokenGetter()) return;

    if (!UserState.user) {
      const user = await fetchUserDetails();
      const user_details = {
        ...user.body.user,
        ...{ orgData: user.body.orgData },
      };
      UserState.setUser(user_details);
    }
    if (!OrganizationState.organizations.length) {
      const orgs = await getOrganizationList();
      OrganizationState.setOrganizations(orgs.response);
    }
  } catch (error) {
    if (error.error?.code === 'NOT_AUTHORIZED') {
      await logout();
    }
  }
};

const route = async url => {
  const urlObj = new URL(url, window.location.origin);
  const params = new URLSearchParams(urlObj.search);

  if (IS_APP()) {
    params.set('isApp', 'true');
  }

  const targetRoute = parseRouteName(urlObj.pathname, urlObj.search);
  const newUrl =
    targetRoute + (params.toString().length > 0 ? `?${params.toString()}` : '');
  const currentUrl = window.location.pathname + window.location.search;
  if (newUrl === currentUrl) {
    return;
  }
  window.history.pushState({ route: targetRoute }, null, newUrl);
  await router();
};

const router = async () => {
  const current_path = window.location.pathname;
  await handleURLToken();
  const route_name = parseRouteName(current_path);

  // Update the URL if the parsed route is different from the current path
  // This ensures the URL reflects the actual route being displayed
  if (route_name !== current_path) {
    const params = new URLSearchParams(window.location.search);

    if (IS_APP()) {
      params.set('isApp', 'true');
    }

    const newUrl =
      route_name +
      (params.toString().length > 0 ? `?${params.toString()}` : '');
    window.history.replaceState({ route: route_name }, null, newUrl);
  }

  await getUserDetails();

  // Dispose previous page before creating new one to prevent memory leaks
  if (currentPage && typeof currentPage.dispose === 'function') {
    try {
      currentPage.dispose();

      // Remove the page DOM element if it exists
      if (currentPage.dom && currentPage.dom.parentNode) {
        currentPage.dom.parentNode.removeChild(currentPage.dom);
      }
    } catch (error) {
      console.error('Error disposing previous page:', error);
    }
  }

  const current_route = Routes[route_name] ?? Routes['/404'];
  const ViewClass = await current_route.view();
  currentPage = new ViewClass.default();

  notifySubscribers(currentPage, current_route.title);
  window.dispatchEvent(new Event('preview:shrink'));
  document.title = current_route.title;
};

const getCurrentPage = () => currentPage;

export {
  getCurrentPage,
  parseRouteName,
  previousRoute,
  route,
  router,
  setPreviousRoute,
  subscribe,
};
